import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsDoctors']
const EMPTY_ARRAY = []

export const useSettingsDoctors = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(`${baseURL}/v2/rest/doctors`, {
        method: 'GET',
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the doctors")
      }
      return res.json()
    },
  })

  return { doctors: data ?? EMPTY_ARRAY, isLoading, isError }
}

export const useUpdateDoctor = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateDoctor, ...rest } = useMutation({
    mutationFn: async ({ id, ...doctorData }) => {
      const res = await fetch(`${baseURL}/v2/rest/settings/doctors/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ doctor: doctorData }),
      })
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update doctor')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { updateDoctor, ...rest }
}

const WEEK_DAY_HOURS_KEY = 'SettingsDoctorWeekDayHours'

export const useDoctorWeekDayHours = doctorId => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [WEEK_DAY_HOURS_KEY, doctorId],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/doctors/${doctorId}/week_day_hours`,
        {
          method: 'GET',
          headers,
        }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the doctor's weekly hours")
      }
      return res.json()
    },
    enabled: !!doctorId,
  })

  return { weekDayHours: data || null, isLoading, isError }
}

export const useUpdateDoctorWeekDayHours = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateWeekDayHours, ...rest } = useMutation({
    mutationFn: async ({ doctorId, weekDayHours }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/doctors/${doctorId}/week_day_hours`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ week_day_hours: weekDayHours }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not update weekly hours')
      }
      return res.json()
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([WEEK_DAY_HOURS_KEY, variables.doctorId])
    },
  })

  return { updateWeekDayHours, ...rest }
}

export const useAutofillDoctorWeekDayHours = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: autofillWeekDayHours, ...rest } = useMutation({
    mutationFn: async doctorId => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/doctors/${doctorId}/week_day_hours/autofill`,
        {
          method: 'POST',
          headers,
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(error.errors || 'Could not autofill weekly hours')
      }
      return res.json()
    },
    onSuccess: (data, doctorId) => {
      queryClient.invalidateQueries([WEEK_DAY_HOURS_KEY, doctorId])
    },
  })

  return { autofillWeekDayHours, ...rest }
}
